import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Portal page access service
 * Handles checking if clients have access to specific portal pages
 */

// Map of portal routes to their access keys
export const PORTAL_PAGE_MAPPING = {
  '/portal/kickoff': 'kickoff',
  '/portal/courses': 'courses',
  '/portal/calendar': 'calendar',
  '/portal/community': 'community',
  '/portal/sales': 'sales',
  '/portal/operations': 'operations',
  '/portal/technology': 'technology',
  '/portal/finance': 'finance',
  '/portal/marketing': 'marketing',
  '/portal/hr': 'hr',
  '/portal/tasks': 'tasks',
  '/portal/messages': 'messages',
  '/portal/my-questions': 'my-questions',
  '/portal/fires': 'fires',
  '/portal/events': 'events',
  '/portal/kpis': 'kpis',
  '/portal/goals': 'goals',
  '/portal/sales-proposals': 'sales-proposals',
  '/portal/sales-referral-programs': 'sales-referral-programs',
  '/portal/sales-scripts': 'sales-scripts',
  '/portal/sales-awards': 'sales-awards',
  '/portal/job-descriptions': 'job-descriptions',
  '/portal/sops': 'sops',
  '/portal/takeoffs': 'takeoffs',
  '/portal/proposal': 'proposal',
  '/portal/roadmap': 'roadmap',
  '/portal/org-chart': 'org-chart',
  '/portal/collection': 'collection',
  '/portal/billing': 'billing',
  '/portal/conference': 'conference'
};

// Pages that have toggles in ClientOverview; all other mapped pages are always full access
export const PORTAL_PAGE_TOGGLE_KEYS = [
  'courses', 'calendar', 'community', 'sales', 'operations', 'technology',
  'finance', 'marketing', 'hr', 'tasks', 'events', 'roadmap', 'collection', 'conference'
];

/**
 * Get client account data by user ID
 * @param {string} userId - The user's UID
 * @returns {Promise<object|null>} - Client account data or null if not found
 */
export const getClientAccountData = async (userId) => {
  try {
    console.log(`[Portal Access] Fetching account data for user: ${userId}`);
    
    // First check if user exists in users collection (portal users)
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (userDoc.exists()) {
      const userData = userDoc.data();
      console.log(`[Portal Access] User found with accountId: ${userData.accountId}`);
      
      // Use accountId or clientId (company/account ID - set during signup process)
      const companyAccountId = userData.accountId || userData.clientId;
      if (companyAccountId) {
        const accountDoc = await getDoc(doc(db, 'accounts', companyAccountId));
        if (accountDoc.exists()) {
          const accountData = accountDoc.data();
          console.log(`[Portal Access] Account data found with portalAccess:`, accountData.portalAccess);
          return accountData;
        } else {
          console.log(`[Portal Access] Account document not found for accountId: ${companyAccountId}`);
        }
      } else {
        console.log(`[Portal Access] User has no accountId/clientId`);
      }
    } else {
      console.log(`[Portal Access] User document not found for userId: ${userId}`);
    }
    
    return null;
  } catch (error) {
    console.error('Error fetching client account data:', error);
    return null;
  }
};

/**
 * Check if client has access to a specific portal page
 * @param {string} userId - The user's UID
 * @param {string} route - The route to check access for
 * @returns {Promise<boolean>} - True if user has access, false otherwise
 */
export const canAccessPortalPage = async (userId, route) => {
  try {
    // Get the access key for this route
    const accessKey = PORTAL_PAGE_MAPPING[route];
    
    // If no mapping exists, allow access (for routes not in the mapping)
    if (!accessKey) {
      console.log(`[Portal Access] No mapping for ${route}, allowing access`);
      return true;
    }
    
    // Get client account data
    const accountData = await getClientAccountData(userId);
    
    // If no account data found, deny access
    if (!accountData) {
      console.log(`[Portal Access] No account data for ${route}, denying access`);
      return false;
    }

    // Pages not in the toggle list are always full access
    if (!PORTAL_PAGE_TOGGLE_KEYS.includes(accessKey)) {
      console.log(`[Portal Access] ${route} (${accessKey}) is always full access`);
      return true;
    }
    
    // Check portal access settings
    const portalAccess = accountData.portalAccess || {};
    
    // If portal access is not configured, use default values
    if (Object.keys(portalAccess).length === 0) {
      console.log(`[Portal Access] No portal access configured for ${route}, using defaults`);
      // Default access based on page type
      const defaultAccess = {
        kickoff: false,
        courses: true,
        calendar: true,
        community: true,
        tasks: true,
        messages: true,
        'my-questions': true,
        fires: true,
        events: true,
        kpis: true,
        goals: true,
        sales: false,
        operations: false,
        technology: false,
        finance: false,
        marketing: false,
        hr: false,
        'sales-proposals': false,
        'sales-referral-programs': false,
        'sales-scripts': false,
        'sales-awards': false,
        'job-descriptions': false,
        sops: false,
        takeoffs: false,
        proposal: false,
        roadmap: false,
        'org-chart': false,
        collection: false,
        billing: false,
        conference: false
      };
      
      const hasAccess = defaultAccess[accessKey] ?? true;
      console.log(`[Portal Access] Default access for ${route} (${accessKey}): ${hasAccess}`);
      return hasAccess;
    }
    
    // Check if the specific page access is enabled
    const hasAccess = portalAccess[accessKey] === true;
    console.log(`[Portal Access] Portal access for ${route} (${accessKey}): ${hasAccess} (value: ${portalAccess[accessKey]})`);
    return hasAccess;
    
  } catch (error) {
    console.error('Error checking portal page access:', error);
    return false;
  }
};

/**
 * Get all accessible portal pages for a client
 * @param {string} userId - The user's UID
 * @returns {Promise<Array<string>>} - Array of accessible route paths
 */
export const getAccessiblePortalPages = async (userId) => {
  try {
    const accessiblePages = [];
    
    for (const [route, accessKey] of Object.entries(PORTAL_PAGE_MAPPING)) {
      const hasAccess = await canAccessPortalPage(userId, route);
      if (hasAccess) {
        accessiblePages.push(route);
      }
    }
    
    return accessiblePages;
  } catch (error) {
    console.error('Error getting accessible portal pages:', error);
    return [];
  }
};

/**
 * Check if client has access to any page in a category
 * @param {string} userId - The user's UID
 * @param {string} category - The category to check (e.g., 'sales', 'marketing')
 * @returns {Promise<boolean>} - True if user has access to any page in category
 */
export const canAccessCategory = async (userId, category) => {
  try {
    const accountData = await getClientAccountData(userId);
    
    if (!accountData) {
      return false;
    }
    
    const portalAccess = accountData.portalAccess || {};
    return portalAccess[category] === true;
    
  } catch (error) {
    console.error('Error checking category access:', error);
    return false;
  }
};
